import { domainLocales } from 'domainLocales'

export function cleanLink(link: string) {
  return (
    link
      .split('?')[0] // remove query
      .split('/')
      .filter((part, index) => {
        if (index === 0 || part) return true
        return false
      })
      .join('/') + '/'
  )
}

export function normalizeWS(s?: string) {
  if (!s) return ''
  const splittedS = s.replace(/\s+/g, ' ').match(/\S+/g)
  return splittedS ? splittedS.join(' ') : ''
}

export function normalizeText(str: string) {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

export const getPopupWrapperDom = () => {
  if (typeof document === 'undefined') return null
  const wrapperId = 'hhg-discover-popup-wrapper'
  if (!document.querySelector('#' + wrapperId)) {
    const newElem = document.createElement('div')
    newElem.id = wrapperId
    document.body.appendChild(newElem)
  }
  return document.querySelector('#' + wrapperId)
}

export const removeAmpFromUrl = (url: string) => {
  const splitted = url.split('?')
  const queryParams = splitted[1]
  if (!queryParams) return url
  const newQueryParams = queryParams
    .split('&')
    .filter((param) => !param.startsWith('amp='))
    .join('&')
  if (!newQueryParams.trim()) return splitted[0]
  return splitted[0] + '?' + newQueryParams
}

// ==== PRE PROCESS HTML FOR ARTICLE ====
function removeStylesExceptPaddingAndMargin(html: string) {
  return html.replace(/style="([^"]*)"/gi, (_match, styles: string) => {
    const newStyles = styles
      .split(';')
      .map((style) => style.trim())
      .filter(
        (style) => style.startsWith('padding') || style.startsWith('margin'),
      )
      .join('; ')

    return newStyles ? `style="${newStyles}"` : ''
  })
}
function processAnchorTags(html: string) {
  return html.replace(
    /<a\s+([^>]*href="([^"]*)")([^>]*)>(.*?)<\/a>/gi,
    (_match, p1, href, p3, content) => {
      // Force all URLs to https
      const httpsUrl = href.replace('http://', 'https://')

      // Escape special characters in the text content
      const escapedContent = content
        .replace(/&/g, '&amp;')
        .replace(/'/g, '&apos;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')

      // Set the necessary attributes
      let newAnchorTag = `<a ${p1.replace(href, httpsUrl)} ${
        p1.includes('data-event-category')
          ? ''
          : ' data-event-category="Internal Link Click on Article"'
      }${
        p1.includes('data-event-action')
          ? ''
          : ` data-event-action="${escapedContent}"`
      }${
        p1.includes('data-event-label') ? '' : ` data-event-label="${httpsUrl}"`
      } ${p3}>${content}</a>`

      // Check if the link is internal
      const isLinkInternal =
        !httpsUrl.startsWith('http') ||
        Object.values(domainLocales)
          .concat(['hellohealthgroup.com', 'localhost:[a-z0-9]+'])
          .some((domain) => {
            const regex = new RegExp(
              `^(https?:\\/\\/)?([a-z0-9]+[.])*${domain}(/|$)`,
              'i',
            )
            return regex.test(httpsUrl)
          })

      if (isLinkInternal) {
        // Remove target and rel attributes if the link is internal
        newAnchorTag = newAnchorTag
          .replace(/\s*target="[^"]*"/i, '')
          .replace(/\s*rel="[^"]*"/i, '')
      }

      return newAnchorTag
    },
  )
}
function processImgTags(html: string) {
  return html.replace(
    /<img\s+([^>]*src="([^"]*)")([^>]*)>/gi,
    (_match, p1, src, p3, offset) => {
      const aboveTheFold = offset < 400
      const shouldLazyLoad = !aboveTheFold && !p3.includes('loading=')

      // Force all URLs to https
      const httpsSrc = src.replace('http://', 'https://')

      // Set the necessary attributes
      const newImgTag = `<img ${p1.replace(src, httpsSrc)} ${
        shouldLazyLoad ? `loading="lazy"` : ''
      } ${p3}>`

      return newImgTag
    },
  )
}
function processOtherHtmlContent(html: string) {
  // Replace 'http://' with 'https://' in img src attributes and add 'loading="lazy"'
  html = html.replace(/<img[^>]*src="http:\/\//g, (match) =>
    match.replace('http://', 'https://').replace('<img', '<img loading="lazy"'),
  )

  // Remove elements with class 'continue-reading-articles'
  html = html.replace(
    /<div[^>]*class="[^"]*continue-reading-articles[^"]*"[^>]*>[\s\S]*?<\/div>/g,
    '',
  )

  // Remove empty <p> elements
  html = html.replace(/<p>\s*<\/p>/g, '')
  html = html.replace(/<p>&nbsp;<\/p>/g, '')

  // Set style attribute to 'display: none' for element with id 'mc4wp-form-1'
  html = html.replace(
    /(<[^>]*id="mc4wp-form-1"[^>]*)>/g,
    '$1 style="display: none">',
  )

  return html
}
function processIframeContent(html: string) {
  // Regex to match <iframe> tags and their src attributes
  html = html.replace(/<iframe[^>]*src="([^"]*)"[^>]*>/g, (match, src) => {
    // Parse the URL and query string
    const parsedUrl = new URL(src, 'http://example.com') // Base URL is required for relative URLs
    parsedUrl.protocol = 'https:'
    parsedUrl.searchParams.set('autoplay', '1')
    parsedUrl.searchParams.set('mute', '1')

    // Construct the new <iframe> tag
    return match
      .replace(src, parsedUrl.toString())
      .replace('<iframe', '<iframe loading="lazy" allow="autoplay"')
  })

  return html
}
// THIS IS consistently 10 times faster than cheerio, tested by running 1000 times
export const preProcessArticleHtml = (html: string) => {
  let processedHtml = removeStylesExceptPaddingAndMargin(html)
  processedHtml = processAnchorTags(processedHtml)
  processedHtml = processImgTags(processedHtml)
  processedHtml = processOtherHtmlContent(processedHtml)
  processedHtml = processIframeContent(processedHtml)
  // processedHtml = sanitize(processedHtml, {
  //   ADD_ATTR: ['name', 'target', 'data-url'],
  //   ADD_TAGS: ['iframe'],
  // }).replace(/<p>\s*<\/p>/g, '')
  return processedHtml
}
